/**
 * WorkspaceLibrarySheet.js
 *
 * Widget catalogue picker for edit mode. Lists ONLY widgets the user can use:
 * the backend's module gate decides (`GET /api/main-dashboard/modules`), and every
 * catalogue entry is bound to a real provider in `main-dashboard/summary`.
 * Searchable by localized title, filterable by module; tapping a row adds the
 * widget and keeps the sheet open so several can be added in a row.
 *
 * @module components/workspaces/WorkspaceLibrarySheet
 */

import React, {useMemo, useState} from 'react';
import {ScrollView, Text, View} from 'react-native';
import {useTranslation} from 'react-i18next';
import PremiumSheet, {SheetChip, SheetInput} from '../management/PremiumSheet';
import {useManagementTheme} from '../../helpers/managementTheme';
import {WORKSPACE_MODULES, WORKSPACE_MODULE_KEYS, WORKSPACE_WIDGETS} from '../../config/workspaceCatalog';
import {isWidgetAllowed} from '../../helpers/workspaces/workspaceData';
import {SheetRow, useWorkspaceLabels} from './WorkspaceUI';

const KIND_ICON = {metric: 'numeric', grid: 'view-grid-outline', trend: 'chart-line', breakdown: 'chart-donut', list: 'format-list-bulleted', actions: 'lightning-bolt-outline'};

/**
 * @param {object} props
 * @param {boolean} props.visible Whether the sheet is shown.
 * @param {() => void} props.onClose Dismiss.
 * @param {?Object.<string, boolean>} props.accessMap Backend module gate (`null` = unknown).
 * @param {Object.<string, number>} props.counts How many of each widget type the open workspace already holds.
 * @param {(type: string) => void} props.onAdd Add a widget.
 * @returns {JSX.Element} The sheet.
 */
export default function WorkspaceLibrarySheet({visible, onClose, accessMap, counts, onAdd}) {
    const {t} = useTranslation();
    const m = useManagementTheme();
    const labels = useWorkspaceLabels();
    const [query, setQuery] = useState('');
    const [moduleKey, setModuleKey] = useState(null);

    const allowed = useMemo(() => WORKSPACE_WIDGETS.filter((definition) => isWidgetAllowed(definition, accessMap)), [accessMap]);
    const modules = useMemo(() => WORKSPACE_MODULE_KEYS.filter((key) => allowed.some((definition) => definition.module === key)), [allowed]);
    const rows = useMemo(() => {
        const needle = query.trim().toLowerCase();

        return allowed
            .filter((definition) => !moduleKey || definition.module === moduleKey)
            .map((definition) => ({definition, title: labels.title(definition.id)}))
            .filter(({title}) => !needle || title.toLowerCase().includes(needle));
    }, [allowed, moduleKey, query, labels]);

    return (
        <PremiumSheet
            visible={visible}
            onClose={onClose}
            title={t('wsd_library_title')}
            subtitle={t('wsd_library_subtitle')}
            icon="widgets-outline"
            headerExtra={(
                <View style={{paddingHorizontal: 20, paddingBottom: 8}}>
                    <SheetInput value={query} onChangeText={setQuery} placeholder={t('wsd_search_placeholder')} autoCorrect={false} />
                    <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={{gap: 8, paddingTop: 10}}>
                        <SheetChip label={t('wsd_all_modules')} active={!moduleKey} onPress={() => setModuleKey(null)} />
                        {modules.map((key) => (
                            <SheetChip
                                key={key}
                                label={labels.text(`module.${key}`, key)}
                                icon={WORKSPACE_MODULES[key].icon}
                                active={moduleKey === key}
                                onPress={() => setModuleKey(moduleKey === key ? null : key)}
                            />
                        ))}
                    </ScrollView>
                </View>
            )}
            hideFooter
        >
            {rows.length === 0 && <Text style={{color: m.colors.subText, textAlign: 'center', marginTop: 16}}>{t('wsd_no_results')}</Text>}
            {rows.map(({definition, title}) => {
                const visual = WORKSPACE_MODULES[definition.module] || {icon: 'view-dashboard-outline', tone: 'primary'};
                const count = counts?.[definition.id] || 0;

                return (
                    <SheetRow
                        key={definition.id}
                        icon={KIND_ICON[definition.kind] || visual.icon}
                        tone={visual.tone}
                        title={title}
                        subtitle={definition.module ? labels.text(`module.${definition.module}`, definition.module) : t('wsd_all_modules')}
                        onPress={() => onAdd(definition.id)}
                        right={count > 0 ? (
                            <Text style={{color: m.colors.primary, fontWeight: '800', fontSize: 12}}>{t('wsd_added')} {count > 1 ? `×${count}` : ''}</Text>
                        ) : null}
                    />
                );
            })}
        </PremiumSheet>
    );
}
